import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

export default function PaymentDetailPage() {
  const { id, buildingId } = useParams();
  const navigate = useNavigate();
  const buildingname = localStorage.getItem("buildingName");
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);

  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);


  // Fetch single payment on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");
    (async () => {
      try {
        const res = await fetch(
          `https://portal.gmacc.co.za/api/payment/${id}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error();
        setPayment(await res.json());
      } catch {
        setError("Could not load payment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Update document title once data arrives
  useEffect(() => {
    if (payment?.cr446_paymentpk) {
      document.title = `${payment.cr446_paymentpk} Detail`;
    }
  }, [payment]);

  if (loading)
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  if (error) return <p className={styles.errorText}>{error}</p>;
  if (!payment) return null;

  // Helpers
  const fmtDate = (d) => {
    try {
      return format(new Date(d), "dd MMM yyyy hh:mm");
    } catch {
      return d || "—";
    }
  };
  const pk = payment.cr446_paymentpk;
  const displayAmt =
    payment["cr446_amount@OData.Community.Display.V1.FormattedValue"] ||
    `R${payment.cr446_amount}`;
  const typeFmt =
    payment["cr446_paymenttype@OData.Community.Display.V1.FormattedValue"];
  const statusFmt =
    payment["cj_progress@OData.Community.Display.V1.FormattedValue"];
  const dateFmt = payment.cj_paymenttime
    ? fmtDate(payment.cj_paymenttime)
    : fmtDate(payment.createdon);

  const statusColorClass =
    {
      "Waiting for Trustee Approval": styles.statusBlue,
      Paid: styles.statusGreen,
      "On Hold": styles.statusOrange,
      Delayed: styles.statusOrange,
      Canceled: styles.statusRed,
      Declined: styles.statusRed,
      "Approval Declined": styles.statusRed,
    }[statusFmt] || "";

  // Related records definitions
  const relatedDefs = [
    {
      type: "Building",
      id: payment._cr446_specifybuilding_value,
      label:
        payment[
          "_cr446_specifybuilding_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "building-info",
    },
    {
      type: "Account",
      id: payment._cj_accountt_value,
      label:
        payment["_cj_accountt_value@OData.Community.Display.V1.FormattedValue"],
      path: "trustee-account",
    },
    {
      type: "Municipality",
      id: payment._cj_municipality_value,
      label:
        payment[
          "_cj_municipality_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "municipality",
    },
    {
      type: "Contractor",
      id: payment._cr446_contractorname_value,
      label:
        payment[
          "_cr446_contractorname_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "contractor",
    },
    {
      type: "Legal",
      id: payment._cj_linkedlegalrecord_value,
      label:
        payment[
          "_cj_linkedlegalrecord_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "legal",
    },
    {
      type: "Maintenance",
      id: payment._cj_linkedmaintenancerecord_value,
      label:
        payment[
          "_cj_linkedmaintenancerecord_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "maintenance",
    },
    {
      type: "Recurring Payment",
      id: payment._cj_recurringpaymentrecord_value,
      label:
        payment[
          "_cj_recurringpaymentrecord_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "recurring",
    },
    {
      type: "Approval",
      id: payment._cj_approvallink_value,
      label:
        payment[
          "_cj_approvallink_value@OData.Community.Display.V1.FormattedValue"
        ],
      path: "building-approvals",
    },
  ].filter((r) => r.id && r.label);

  const renderField = (label, val) =>
    val != null && String(val).trim() !== "" ? (
      <div key={label} className={styles["input-group"]}>
        <h3>{label}:</h3>
        <span className={styles.value}>{val}</span>
      </div>
    ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>{payment.cr446_paymentpk} Detail</h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>
      <div className={styles.shell}>
        {/* LEFT SIDEBAR (Owner / Trustee / Contractor portal menu) */}
        <PortalSidebar
  activePortal="trustee"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* SUMMARY STRIP */}
            <div
              className={`${styles.paymentHeaderCard} ${statusColorClass}`}
              data-status={statusFmt}
            >
              {/* BACK BUTTON */}
              <button
                className={styles.paymentBackBtn}
                onClick={() => navigate(-1)}
              >
                Back
              </button>

              {/* TITLE */}
              <h1 className={styles.paymentHeaderTitle}>PAYMENT DETAILS</h1>

              {/* DIVIDER */}
              <div className={styles.paymentHeaderDivider} />

              {/* HEADER LABELS — AUTO GRID LIKE JOB DETAILS */}
              <div
                className={styles.paymentHeaderGridAuto}
                style={{ "--col-count": 5 }} // because you have 5 fields
              >
                <div className={styles.paymentHeaderLabelItem}>
                  Payment Number:
                </div>
                <div className={styles.paymentHeaderLabelItem}>Amount:</div>
                <div className={styles.paymentHeaderLabelItem}>
                  Payment Type:
                </div>
                <div className={styles.paymentHeaderLabelItem}>Progress:</div>
                <div className={styles.paymentHeaderLabelItem}>Date:</div>
              </div>

              {/* VALUE PILL */}
              <div className={styles.paymentHeaderValuePill}>
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": 5 }}
                >
                  <div className={styles.paymentHeaderValueItem}>{pk}</div>
                  <div className={styles.paymentHeaderValueItem}>
                    {displayAmt}
                  </div>
                  <div className={styles.paymentHeaderValueItem}>{typeFmt}</div>
                  <div className={styles.paymentHeaderValueItem}>
                    {statusFmt}
                  </div>
                  <div className={styles.paymentHeaderValueItem}>{dateFmt}</div>
                </div>
              </div>
            </div>

            {/* DETAILS GRID */}
            <div className={styles.paymentCardGrid}>
              {/* LEFT BLUE CARD */}
              <div className={styles.paymentCardDark}>
                <h2 className={styles.cardSectionTitle}>Payment Details</h2>

                {/* Their Reference */}
                <div className={styles.inputGroup}>
                  <h3 className={styles.inputLabel}>Their Reference:</h3>
                  <div className={styles.inputField}>
                    {payment.cr446_theirreference || "—"}
                  </div>
                </div>

                {/* Our Reference */}
                <div className={styles.inputGroup}>
                  <h3 className={styles.inputLabel}>Our Reference:</h3>
                  <div className={styles.inputField}>
                    {payment.cr446_ourreference || "—"}
                  </div>
                </div>

                {/* Comment */}
                <div className={styles.inputGroup}>
                  <h3 className={styles.inputLabel}>Comment:</h3>
                  <div className={styles.inputField}>
                    {payment.cr446_comment || "—"}
                  </div>
                </div>

                {/* Amount */}
                <div className={styles.inputGroup}>
                  <h3 className={styles.inputLabel}>Amount:</h3>
                  <div className={styles.inputField}>{displayAmt}</div>
                </div>

                {/* Attachment */}
                {payment.cj_fileattachmentlink && (
                  <div className={styles.inputGroup}>
                    <h3 className={styles.inputLabel}>Attachment:</h3>
                    <div className={styles.inputField}>
                      <a
                        href={payment.cj_fileattachmentlink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.relatedLinkDark}
                      >
                        Download Attachment
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* FROM BANK DETAILS */}
              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>
                  From Bank Details
                </h2>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Account Name:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment[
                      "_cr446_frombankaccountnumber_value@OData.Community.Display.V1.FormattedValue"
                    ] || "—"}
                  </div>
                </div>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Account Number:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment.cj_bankaccountnumberfrom || "—"}
                  </div>
                </div>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Bank Name:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment[
                      "_cr446_bankusedforaccounts_value@OData.Community.Display.V1.FormattedValue"
                    ] || "—"}
                  </div>
                </div>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Branch Code:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment.cj_branchcodefrom || "—"}
                  </div>
                </div>
              </div>

              {/* TO BANK DETAILS */}
              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>
                  To Bank Details
                </h2>

                {typeFmt === "Municipality" ? (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>
                      Paid to Municipality:
                    </h3>
                    <div className={styles.inputFieldLight}>Yes</div>
                  </div>
                ) : (
                  <>
                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>Account Name:</h3>
                      <div className={styles.inputFieldLight}>
                        {payment[
                          "_cj_beneficiarybankaccname1_value@OData.Community.Display.V1.FormattedValue"
                        ] || "—"}
                      </div>
                    </div>

                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>
                        Account Number:
                      </h3>
                      <div className={styles.inputFieldLight}>
                        {payment.cj_toaccountnumber1 || "—"}
                      </div>
                    </div>

                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>Bank Name:</h3>
                      <div className={styles.inputFieldLight}>
                        {payment[
                          "_cj_banknameto1_value@OData.Community.Display.V1.FormattedValue"
                        ] || "—"}
                      </div>
                    </div>

                    <div className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>Branch Code:</h3>
                      <div className={styles.inputFieldLight}>
                        {payment.cj_branchcodeto1 || "—"}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* RELATED RECORDS */}
              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>
                  Related Records
                </h2>

                {relatedDefs.map(({ type, id, label, path }) => {
                  const isRecurring = type === "Recurring Payment";
                  const url = isRecurring
                    ? null
                    : path === "building-approvals" || path === "building-info"
                    ? `/${path}/${id}`
                    : `/${path}/${id}/${buildingId}`;

                  return (
                    <div key={id} className={styles.inputGroupLight}>
                      <h3 className={styles.inputLabelLight}>{type}:</h3>

                      <div className={styles.inputFieldLight}>
                        {isRecurring ? (
                          <span>{label}</span>
                        ) : (
                          <span
                            className={styles.relatedLink}
                            onClick={() => url && navigate(url)}
                          >
                            {label}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* STATUS */}
              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>Status</h2>

                {payment[
                  "cr446_onhold@OData.Community.Display.V1.FormattedValue"
                ] === "Yes" && (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>On Hold:</h3>
                    <div className={styles.inputFieldLight}>Yes</div>
                  </div>
                )}

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Paid:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment[
                      "cj_paid@OData.Community.Display.V1.FormattedValue"
                    ] || "—"}
                  </div>
                </div>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Approved:</h3>
                  <div className={styles.inputFieldLight}>
                    {payment[
                      "cr446_approved@OData.Community.Display.V1.FormattedValue"
                    ] || "—"}
                  </div>
                </div>

                {payment.cr446_approvedby && (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>Approved By:</h3>
                    <div className={styles.inputFieldLight}>
                      {payment.cr446_approvedby}
                    </div>
                  </div>
                )}
              </div>

              {/* TIMESTAMPS */}
              <div className={styles.paymentCard}>
                <h2 className={styles.cardSectionTitleLight}>Timestamps</h2>

                <div className={styles.inputGroupLight}>
                  <h3 className={styles.inputLabelLight}>Created On:</h3>
                  <div className={styles.inputFieldLight}>
                    {fmtDate(payment.createdon)}
                  </div>
                </div>

                {payment.cj_paymentsubmissiontime && (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>Submitted:</h3>
                    <div className={styles.inputFieldLight}>
                      {fmtDate(payment.cj_paymentsubmissiontime)}
                    </div>
                  </div>
                )}

                {payment.cj_confirmationtime && (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>Confirmed:</h3>
                    <div className={styles.inputFieldLight}>
                      {fmtDate(payment.cj_confirmationtime)}
                    </div>
                  </div>
                )}

                {payment.cj_paymenttime && (
                  <div className={styles.inputGroupLight}>
                    <h3 className={styles.inputLabelLight}>Paid Time:</h3>
                    <div className={styles.inputFieldLight}>
                      {fmtDate(payment.cj_paymenttime)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
